/**
 * Тріщини: what a block that is being broken looks like.
 *
 * A block with durability needs to say how far in you are, and a number on
 * the screen would be the wrong answer - you are looking at the block, not
 * at the HUD. So the block cracks: a first split, then a fork, then a web,
 * then a face about to fall in.
 *
 * Painted rather than drawn from a file, like every other picture in this
 * game, and painted as *fissures* rather than as a grid of scratches: the
 * lines start from one point on the face and wander outwards, the way a
 * struck thing actually splits. Each has a pale lip on one side, which is
 * what makes it read as a crack with depth rather than as a dark scribble.
 *
 * Pure: a rectangle of bytes, four times over. It has never seen a texture.
 */

export const CRACK_SIZE = 16;
export const CRACK_STAGES = 4;

/** Where the splits start from, and which way each one wanders off. */
const FISSURES: readonly { readonly angle: number; readonly bend: number }[] = [
  { angle: -0.4, bend: 0.55 },
  { angle: 2.1, bend: -0.5 },
  { angle: 3.9, bend: 0.62 },
  { angle: 1.1, bend: -0.7 },
  { angle: 5.2, bend: 0.4 },
  { angle: 2.9, bend: -0.35 },
];

/**
 * One stage of cracking, 1 to `CRACK_STAGES`.
 *
 * Stage one is a single split a third of the way across; by stage four every
 * fissure is drawn full length and the corners are chipped. The wandering is
 * deterministic - the same block always cracks the same way - because a face
 * that re-scribbles itself every frame reads as static, not as damage.
 */
export function paintCracks(stage: number): Uint8ClampedArray {
  const px = new Uint8ClampedArray(CRACK_SIZE * CRACK_SIZE * 4);
  const step = Math.max(1, Math.min(CRACK_STAGES, Math.round(stage)));
  const how = step / CRACK_STAGES;

  const put = (x: number, y: number, dark: boolean): void => {
    const ix = Math.round(x);
    const iy = Math.round(y);
    if (ix < 0 || iy < 0 || ix >= CRACK_SIZE || iy >= CRACK_SIZE) return;
    const at = (iy * CRACK_SIZE + ix) * 4;
    // The lip is only ever painted where nothing darker is already.
    if (!dark && px[at + 3]! > 0) return;
    px[at] = dark ? 12 : 168;
    px[at + 1] = dark ? 10 : 160;
    px[at + 2] = dark ? 9 : 150;
    px[at + 3] = dark ? 235 : 150;
  };

  const middle = CRACK_SIZE / 2 - 0.5;
  // Two fissures at the first stage and all six by the last.
  const many = Math.max(2, Math.round(FISSURES.length * how));
  const reach = (CRACK_SIZE / 2) * (0.35 + 0.65 * how);

  for (let i = 0; i < many; i++) {
    const split = FISSURES[i]!;
    let x = middle;
    let y = middle;
    let angle = split.angle;
    for (let along = 0; along < reach; along++) {
      // A crack does not travel straight: it leans as it goes, and the lean
      // is what stops six of them looking like a compass rose.
      angle += split.bend * (along % 2 === 0 ? 0.25 : -0.18);
      x += Math.cos(angle);
      y += Math.sin(angle);
      put(x, y, true);
      /*
       * And by the last stage the splits are open rather than hairline.
       *
       * Something had to separate "nearly through" from "halfway", and the
       * first attempt chipped the four corners - which on a block face read
       * as rounded corners, not as damage. A crack that has *opened* is the
       * honest picture of a face about to come out.
       */
      if (step === CRACK_STAGES) {
        put(x + Math.cos(angle - Math.PI / 2), y + Math.sin(angle - Math.PI / 2),
          true);
      }
      // The pale side, always the same side of the line, so the light in
      // the picture comes from one place.
      put(x + Math.cos(angle + Math.PI / 2), y + Math.sin(angle + Math.PI / 2),
        false);
    }
  }

  return px;
}
